import { useEffect, useId, useState } from 'react';
import { useIsCodeFenceIncomplete } from 'streamdown';
import mermaid, { type MermaidConfig } from 'mermaid';

type ChatMermaidPreviewProps = {
  chart: string;
};

const MERMAID_CONFIG: MermaidConfig = {
  startOnLoad: false,
  securityLevel: 'strict',
  theme: 'base',
  suppressErrorRendering: true,
  fontFamily: 'inherit',
  themeVariables: {
    primaryColor: '#0A8BC7',
    primaryTextColor: '#0f172a',
    primaryBorderColor: '#0A8BC7',
    lineColor: '#71717a',
    secondaryColor: '#f4f4f5',
    tertiaryColor: '#ffffff',
  },
};

function DrawingPlaceholder() {
  return (
    <div
      className="chat-markdown-mermaid chat-mermaid-pending my-3 text-xs text-[#0A8BC7]"
      role="status"
    >
      Drawing diagram…
    </div>
  );
}

function ChatMermaidPreview({ chart }: ChatMermaidPreviewProps) {
  const reactId = useId().replace(/:/g, '');
  const isIncomplete = useIsCodeFenceIncomplete();
  const [svg, setSvg] = useState('');
  const [error, setError] = useState<string | null>(null);
  const source = chart.trim();

  useEffect(() => {
    if (isIncomplete || !source) {
      setSvg('');
      setError(null);
      return undefined;
    }

    let cancelled = false;
    const renderId = `grigo-mermaid-${reactId}`;

    (async () => {
      try {
        mermaid.initialize(MERMAID_CONFIG);
        const result = await mermaid.render(renderId, source);
        if (!cancelled) {
          setSvg(result.svg);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setSvg('');
          setError(err instanceof Error ? err.message : 'Invalid diagram');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [source, isIncomplete, reactId]);

  const copySource = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(source).catch(() => undefined);
    }
  };

  if (isIncomplete || !source) {
    return <DrawingPlaceholder />;
  }

  if (error) {
    return (
      <div className="chat-markdown-mermaid my-3">
        <p className="text-sm text-zinc-700">Could not render this diagram.</p>
        <p className="mt-1 text-xs text-zinc-500">{error}</p>
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-800">
          {source}
        </pre>
      </div>
    );
  }

  if (!svg) {
    return <DrawingPlaceholder />;
  }

  return (
    <div className="chat-markdown-mermaid">
      <div className="mb-1 flex justify-end">
        <button
          type="button"
          className="text-xs text-[#0A8BC7] hover:underline"
          onClick={copySource}
        >
          Copy source
        </button>
      </div>
      <div className="chat-mermaid-canvas" dangerouslySetInnerHTML={{ __html: svg }} />
    </div>
  );
}

export default ChatMermaidPreview;
